import React from 'react';
import PropTypes from 'prop-types';

import { Modal, Spinner, Card } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import {
  white,
  backgroundGrey5,
  softBlue,
  softBlueLight,
  shadowSoftBlue,
  warmGray,
  greyBorder,
} from 'lib/css/colors';

function DistributorModal({ show, onHide, distributors, loading, title }) {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className={css(styles.modalHeader)}>
        <Modal.Title className={css(styles.modalTitle)}>
          <div className={css(styles.titleIcon)}>🏢</div>
          {title} - Distributors
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={css(styles.modalBody)}>
        {loading ? (
          <div className={css(styles.loadingContainer)}>
            <Spinner animation="border" role="status" className={css(styles.spinner)} />
            <div className={css(styles.loadingText)}>Loading distributors...</div>
          </div>
        ) : distributors && distributors.length > 0 ? (
          <div className={css(styles.distributorsGrid)}>
            {distributors.map(distributor => (
              <Card key={distributor.id} className={css(styles.distributorCard)}>
                <div className={css(styles.cardContent)}>
                  <div className={css(styles.logoContainer)}>
                    {distributor.logo_url ? (
                      <img
                        src={distributor.logo_url}
                        alt={`${distributor.name} logo`}
                        className={css(styles.logo)}
                        onError={e => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={css(styles.logoPlaceholder)}>
                        {distributor.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className={css(styles.distributorInfo)}>
                    <div className={css(styles.distributorName)}>{distributor.name}</div>
                    <div className={css(styles.productCount)}>
                      {distributor.product_count}{' '}
                      {distributor.product_count === 1 ? 'product' : 'products'}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className={css(styles.emptyState)}>
            <div className={css(styles.emptyIcon)}>📦</div>
            <div className={css(styles.emptyTitle)}>No distributors found</div>
            <div className={css(styles.emptyMessage)}>
              There are no distributors associated with this{' '}
              {title?.toLowerCase().includes('manufacturer') ? 'manufacturer' : 'brand'}.
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalHeader: {
    backgroundColor: backgroundGrey5,
    borderBottom: `1px solid ${greyBorder}`,
    borderRadius: '8px 8px 0 0',
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px',
    fontWeight: '600',
    color: softBlue,
  },
  titleIcon: {
    fontSize: '20px',
    marginRight: '8px',
  },
  modalBody: {
    padding: '20px',
    backgroundColor: white,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
  },
  spinner: {
    color: softBlue,
    marginBottom: '16px',
  },
  loadingText: {
    color: warmGray,
    fontSize: '14px',
  },
  distributorsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  distributorCard: {
    border: `1px solid ${softBlueLight}`,
    borderRadius: '12px',
    transition: 'all 0.2s ease-in-out',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${shadowSoftBlue}`,
      borderColor: softBlue,
    },
  },
  cardContent: {
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: softBlueLight,
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  logoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: softBlue,
    color: white,
    fontSize: '18px',
    fontWeight: '600',
  },
  distributorInfo: {
    flex: 1,
    minWidth: 0, // Allow text to truncate
  },
  distributorName: {
    fontSize: '16px',
    fontWeight: '600',
    color: softBlue,
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  productCount: {
    fontSize: '12px',
    color: warmGray,
    fontWeight: '500',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: softBlue,
    marginBottom: '8px',
  },
  emptyMessage: {
    fontSize: '14px',
    color: warmGray,
    maxWidth: '400px',
    lineHeight: '1.5',
  },
});

DistributorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  distributors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      logo_url: PropTypes.string,
      product_count: PropTypes.number.isRequired,
    }),
  ),
  loading: PropTypes.bool,
  title: PropTypes.string,
};

DistributorModal.defaultProps = {
  distributors: [],
  loading: false,
  title: '',
};

export default DistributorModal;
